import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { obtenirEcho } from './echo';

/**
 * La séance partagée du salon vocal.
 *
 * Un principe, et tout le reste en découle : **seul le maître écrit**. Les
 * autres lisent et se calent. Deux clients qui corrigeraient chacun leur
 * dérive se corrigeraient mutuellement, et la séance oscillerait sans jamais
 * se stabiliser.
 *
 * La position n'est pas diffusée en continu. Le serveur retient une position
 * et l'instant où elle était vraie ; le reste se déduit du temps écoulé. Un
 * client qui arrive en retard tombe donc juste sans rien demander, et une
 * coupure réseau ne décale personne.
 *
 * Reste le décalage d'horloge : deux machines ne sont jamais à la même
 * milliseconde. Sans le mesurer, un client corrigerait son horloge en croyant
 * corriger sa lecture. On l'estime à chaque lecture d'état, par la méthode la
 * plus simple qui marche — l'aller-retour est supposé symétrique.
 */

const BATTEMENT_MS = 10_000;
const RESYNC_MS = 30_000;

export function useSeance({ instanceId, utilisateur }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);

  // Décalage entre l'horloge du serveur et la nôtre, en millisecondes.
  const decalageRef = useRef(0);
  const etatRef = useRef(null);
  etatRef.current = etat;

  // `prendreLaMain` est definie plus bas ; l'effet d'entree en a besoin sans
  // pouvoir en dependre, sous peine de se relancer a chaque rendu.
  const prendreLaMainRef = useRef(null);

  const jeSuisMaitre = Boolean(
    etat?.master_user_id && utilisateur?.id && etat.master_user_id === utilisateur.id,
  );

  const chemin = `/discord/activity/session/${encodeURIComponent(instanceId)}`;

  /* ── Lecture de l'état ────────────────────────────────────────────── */

  const relire = useCallback(async () => {
    const depart = Date.now();
    const donnees = await api.get(chemin);
    const retour = Date.now();

    if (donnees?.state?.server_time) {
      // Milieu de l'aller-retour : la meilleure estimation de « quand » le
      // serveur a répondu, sans protocole d'horloge à part entière.
      decalageRef.current = donnees.state.server_time - (depart + retour) / 2;
    }

    setEtat(donnees.state);
    return donnees.state;
  }, [chemin]);

  useEffect(() => {
    let vivant = true;

    relire()
      .then((etatInitial) => {
        // Le premier arrive prend la main, avant meme d'avoir choisi quoi que
        // ce soit. Attendre le premier film laissait n'importe qui lancer a la
        // place de celui qui avait ouvert la seance.
        //
        // Uniquement s'il n'y a pas de film : reprendre une seance en cours
        // parce que le maitre s'est tu doit rester un geste delibere, d'ou le
        // bouton « Prendre la main » plutot qu'un vol silencieux.
        if (vivant && etatInitial?.master_absent && !etatInitial?.movie_id) {
          prendreLaMainRef.current?.();
        }
      })
      .catch((e) => { if (vivant) setErreur(e.message); });

    // Filet : si un message temps réel se perd, on ne reste pas décalé
    // indéfiniment pour autant.
    const minuteur = setInterval(() => { relire().catch(() => {}); }, RESYNC_MS);

    return () => { vivant = false; clearInterval(minuteur); };
  }, [relire]);

  /* ── Temps réel ───────────────────────────────────────────────────── */

  useEffect(() => {
    const nom = `activity.${instanceId}`;
    let canal;

    try {
      canal = obtenirEcho().channel(nom);
      canal.listen('.sync.updated', (donnees) => {
        if (donnees?.state) setEtat(donnees.state);
      });
    } catch (e) {
      // Sans temps réel la séance reste utilisable : la relecture périodique
      // prend le relais, en moins réactif. Ce n'est pas une raison d'échouer.
      console.error('Reverb indisponible :', e);
    }

    return () => {
      try { obtenirEcho().leave(nom); } catch { /* déjà parti */ }
    };
  }, [instanceId]);

  /* ── Écriture, réservée au maître ─────────────────────────────────── */

  const pousser = useCallback(async (changement) => {
    const courant = etatRef.current;

    const corps = {
      is_playing: changement.is_playing ?? courant?.is_playing ?? false,
      position: changement.position ?? 0,
      ...(changement.film
        ? {
            movie_id: String(changement.film.id),
            movie_title: changement.film.title ?? '',
            movie_poster: changement.film.poster ?? '',
          }
        : {}),
    };

    try {
      const donnees = await api.post(chemin, corps);
      setEtat(donnees.state);
    } catch (e) {
      // 409 : quelqu'un d'autre tient la main. Le serveur renvoie l'état
      // courant avec son refus, on s'y range plutôt que d'insister.
      if (e.statut === 409) await relire().catch(() => {});
      else setErreur(e.message);
    }
  }, [chemin, relire]);

  const lancerFilm = useCallback((film) => pousser({ film, is_playing: true, position: 0 }), [pousser]);

  const prendreLaMain = useCallback(async () => {
    try {
      const donnees = await api.post(`${chemin}/claim`, {});
      setEtat(donnees.state);
    } catch (e) {
      if (e.statut === 409) await relire().catch(() => {});
    }
  }, [chemin, relire]);

  prendreLaMainRef.current = prendreLaMain;

  const quitterLeFilm = useCallback(
    () => pousser({ film: { id: '', title: '', poster: '' }, is_playing: false, position: 0 }),
    [pousser],
  );

  /* ── Battement du maître ──────────────────────────────────────────── */

  useEffect(() => {
    if (!jeSuisMaitre) return undefined;

    const minuteur = setInterval(() => {
      api.post(`${chemin}/heartbeat`, {}).catch(() => {});
    }, BATTEMENT_MS);

    return () => clearInterval(minuteur);
  }, [jeSuisMaitre, chemin]);

  /* ── Où devrait-on en être, maintenant ? ──────────────────────────── */

  const positionAttendue = useCallback(() => {
    const courant = etatRef.current;
    if (!courant?.movie_id) return null;
    if (!courant.is_playing) return courant.position;

    const maintenantServeur = Date.now() + decalageRef.current;
    return courant.position + (maintenantServeur - courant.server_time) / 1000;
  }, []);

  const film = etat?.movie_id
    ? { id: etat.movie_id, title: etat.movie_title, poster: etat.movie_poster }
    : null;

  return {
    etat,
    film,
    erreur,
    jeSuisMaitre,
    maitreAbsent: Boolean(etat?.master_absent),
    nomDuMaitre: etat?.master_name ?? null,
    positionAttendue,
    pousser,
    lancerFilm,
    prendreLaMain,
    quitterLeFilm,
  };
}
